import { PageController } from '@client/components/_core/layout/PageController';
import { VDropdown } from '@client/components/_shared/forms/view-form/VDropdown';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { CurrencyInput } from '@client/components/_ui/data-entry/CurrencyInput';
import { User } from '@client/components/_ui/display/User';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { createFormStore } from '@client/stores/FormStore';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { buildCurrencyInputRules, cloneDeep } from '@client/utils';
import { EXCESS_PAYMENT_METHODS_LABELS, EXCESS_PAYMENT_STATUSES_LABELS } from '@client/utils/financials';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { EXCESS_PAYMENT_STATUSES, type ExcessPaymentStatus } from '@shared/constants/business/excess-payment';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Select, Skeleton, Tooltip } from 'antd';
import type { Decimal } from 'decimal.js';
import { CreditCard, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import { match } from 'ts-pattern';

export const Route = createFileRoute(
    '/console/$orgId/claims/details/$id/financials/excesses/details/$excessId/payments',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Linked payments',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ExcessPayments />
        </PageController>
    );
}

const formStore = createFormStore<Record<string, ExcessPaymentStatus>>();

const columnHelper = createColumnHelper<
    NonNullable<RouterOutputs['excessPayment']['listExcessPayments']>['rows'][number],
    NonNullable<RouterInputs['excessPayment']['listExcessPayments']['orderBy']>
>();

type ExcessPayment = {
    amount: Decimal;
    status: ExcessPaymentStatus;
};

const FormItem = Form.Item<ExcessPayment>;

function ExcessPayments() {
    const { excessId } = Route.useParams();
    const { trpcUtils } = useTrpc();
    const { data: excess } = trpc.excess.getExcess.useQuery({
        id: excessId,
    });
    const { mutate: createExcessPayment, isPending: isPendingCreateExcessPayment } =
        trpc.excessPayment.createExcessPayment.useMutation();
    const { mutate: deleteExcessPayment, isPending: isPendingDeleteExcessPayment } =
        trpc.excessPayment.deleteExcessPayment.useMutation();
    const { mutateAsync: updateExcessPayment } = trpc.excessPayment.updateExcessPayment.useMutation();
    const { formatDate, formatCurrency } = useFormatter();

    const [input, setInput] = useState<RouterInputs['excessPayment']['listExcessPayments']>({
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { id: 'desc' },
    });

    const { data, isPending, error, refetch } = trpc.excessPayment.listExcessPayments.useQuery({
        ...input,
        where: {
            ...input?.where,
            excessId,
        },
    });

    const [form] = Form.useForm<ExcessPayment>();

    const { formData } = formStore.useInit({
        entityLabel: 'Excess contribution',
        data: useMemo(
            () => (data ? Object.fromEntries(data.rows.map((item) => [String(item.id), item.status])) : undefined),
            [data],
        ),
        isLoading: isPending,

        async onReload() {
            await refetch();
        },

        async onSave(_, changedValues) {
            try {
                if (!data) {
                    throw new Error('Data not found.');
                }

                const [id, status] = Object.entries(changedValues)[0];

                if (!status) {
                    throw new Error('Status is required.');
                }

                const index = data.rows.findIndex((item) => String(item.id) === id);

                if (index === -1) {
                    throw new Error(`There is no field with name ${id}.`);
                }

                const payload = cloneDeep(data.rows);
                payload[index] = { ...payload[index], status };

                await updateExcessPayment({
                    id: BigInt(id),
                    set: {
                        status,
                    },
                });

                await refetch();
                await trpcUtils.excess.invalidate();
                void trpcUtils.claim.getFinancialSummary.invalidate();

                return { success: true };
            } catch (e) {
                return { success: false, error: e };
            }
        },
    });

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isPending || isPendingDeleteExcessPayment,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        // A descriptive key: the claim page's own table may hold `q` underneath this overlay (CUR-1341).
        urlKey: 'contributions',
        queryError: error,
        emptyState: { noRecordsTitle: 'No contributions recorded for this excess' },
        pagination: {
            position: 'bottom',
        },
        columns: [
            columnHelper.accessor('createdAt', {
                id: 'createdAt',
                header: 'Date created',
                sortable: 'createdAt',
                cell: ({ row }) => formatDate(row.createdAt, 'dd MMM yyyy'),
            }),
            columnHelper.accessor((row) => row.CreatedBy?.firstName, {
                id: 'createdBy',
                header: 'Created by',
                sortable: 'CreatedBy.firstName',
                cell: ({ row }) => (
                    <User
                        userId={row.CreatedBy?.userId}
                        photoUrl={row.CreatedBy?.photoUrl}
                        name={row.CreatedBy?.fullName}
                    />
                ),
            }),
            columnHelper.accessor('method', {
                id: 'method',
                header: 'Contribution method',
                sortable: 'method',
                cell: ({ row }) => EXCESS_PAYMENT_METHODS_LABELS[row.method],
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ row }) =>
                    formData?.[String(row.id)] && (
                        <VDropdown
                            field={String(row.id)}
                            formStore={formStore}
                            formatValueFn={(value) => EXCESS_PAYMENT_STATUSES_LABELS[value as ExcessPaymentStatus]}
                            options={Object.entries(EXCESS_PAYMENT_STATUSES_LABELS).map(([key, value]) => ({
                                label: value,
                                value: key,
                            }))}
                            tooltip={
                                excess?.isDismissed
                                    ? 'Contribution status cannot be changed since an excess has been cancelled or waived.'
                                    : undefined
                            }
                            isReadOnly={excess?.isDismissed}
                        />
                    ),
                className: 'w-44',
            }),
            columnHelper.accessor('increasingAdjustment', {
                id: 'increasingAdjustment',
                header: 'IA',
                cell: ({ row }) => formatCurrency(row.increasingAdjustment),
            }),
            columnHelper.accessor('amount', {
                id: 'amount',
                header: 'Amount',
                sortable: 'amount',
                cell: ({ row }) => formatCurrency(row.amount),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.Delete
                        disabled={excess?.isDismissed || !row.canDelete}
                        tooltip={match({ dismissed: !!excess?.isDismissed, canDelete: row.canDelete })
                            .with(
                                { dismissed: true },
                                () =>
                                    'This contribution cannot be deleted since an excess has been cancelled or waived.',
                            )
                            .with(
                                { canDelete: false },
                                () =>
                                    'This contribution cannot be deleted because its compensation is initialised or its claim is closed.',
                            )
                            .otherwise(() => undefined)}
                        onConfirm={(callback) =>
                            deleteExcessPayment(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        void refetch();
                                        void trpcUtils.excess.invalidate();
                                        void trpcUtils.compensation.invalidate();
                                        void trpcUtils.compensationPayment.invalidate();
                                        void trpcUtils.claim.getFinancialSummary.invalidate();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            )
                        }
                    />
                ),
            }),
        ],
    });

    if (isPending) {
        return <Skeleton active />;
    }

    return (
        <PrismaTable {...tableProps}>
            <PrismaTable.Header
                icon={<CreditCard className="card-title-icon" />}
                title="Excess contributions"
                rightContent={
                    <Form
                        form={form}
                        initialValues={{
                            status: EXCESS_PAYMENT_STATUSES.PENDING,
                        }}
                        disabled={excess?.isDismissed || excess?.remainingAmountDraft.eq(0)}
                        className="flex items-center gap-2"
                        onFinish={(values) => {
                            message.loading('Saving...');

                            createExcessPayment(
                                {
                                    amount: values.amount,
                                    status: values.status,
                                    method: 'DIRECT_TO_INSURER',
                                    excessId,
                                },
                                {
                                    onSuccess() {
                                        message.success('Saved.');
                                        form.resetFields();
                                        void refetch();
                                        void trpcUtils.excess.invalidate();
                                        void trpcUtils.compensation.invalidate();
                                        void trpcUtils.compensationPayment.invalidate();
                                        void trpcUtils.claim.getFinancialSummary.invalidate();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    >
                        <FormItem
                            name="amount"
                            rules={buildCurrencyInputRules({
                                isRequired: true,
                                gtZero: true,
                                max: excess?.remainingAmountDraft,
                            })}
                            noStyle
                        >
                            <CurrencyInput
                                className="w-80"
                                addonAfter={
                                    <Select
                                        size="small"
                                        defaultValue={EXCESS_PAYMENT_STATUSES.PENDING}
                                        popupMatchSelectWidth={false}
                                        options={[
                                            {
                                                label: 'Pending',
                                                value: EXCESS_PAYMENT_STATUSES.PENDING,
                                            },
                                            {
                                                label: 'Fully settled',
                                                value: EXCESS_PAYMENT_STATUSES.FULLY_SETTLED,
                                            },
                                        ]}
                                        onChange={(value) => form.setFieldsValue({ status: value })}
                                    />
                                }
                            />
                        </FormItem>
                        <FormItem name="status" hidden />
                        <Tooltip
                            title={
                                excess?.isDismissed
                                    ? 'Contributions cannot be added since an excess has been cancelled or waived.'
                                    : excess?.remainingAmountDraft.eq(0)
                                      ? 'Contributions cannot be added since the excess has been fully settled.'
                                      : 'Add contribution directly to insurer'
                            }
                        >
                            <Button
                                type="primary"
                                icon={<Plus />}
                                size="small"
                                htmlType="submit"
                                loading={isPendingCreateExcessPayment}
                                disabled={excess?.isDismissed || excess?.remainingAmountDraft.eq(0)}
                            >
                                Add contribution
                            </Button>
                        </Tooltip>
                    </Form>
                }
            />
        </PrismaTable>
    );
}
